import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FileDown } from "lucide-react";
import { RevealLine } from "@/components/RevealLine";
import { LegalCrossLinks } from "@/pages/Terms";

const SECTIONS = [
  {
    title: "Introduction",
    body: "At Offcut, we take your privacy seriously. Our Privacy Policy explains how we collect, use, protect and manage your personal information.",
  },
  {
    title: "What We Collect",
    body: "We collect information reasonably necessary to provide Offcut, including your name, email address, account information, subscription information and technical information.",
  },
  {
    title: "Your Content",
    body: "Depending on the features you use, Offcut may process project information, measurements, notes, drawings, photographs, uploaded files, AI prompts, voice input and AI-generated responses.",
  },
  {
    title: "How We Use Your Information",
    body: "We use information to provide and operate Offcut, manage accounts and subscriptions, provide support, maintain security, prevent misuse, improve the Services and comply with legal obligations.",
  },
  {
    title: "Third-Party Providers",
    body: "We use trusted providers for services such as cloud hosting, database storage, authentication, email, subscriptions, AI processing, analytics and security.",
  },
  {
    title: "AI Features",
    body: "When you use AI features, relevant information may be processed by our AI service providers. Where reasonably practicable, we configure providers not to use customer content to train publicly available AI models unless you have separately agreed to such use or the provider's applicable terms require otherwise.",
  },
  {
    title: "Payment Information",
    body: "Payments are processed by Apple, Google or another authorised payment provider. Offcut does not collect or store your complete payment card details.",
  },
  {
    title: "Your Information Is Not Sold",
    body: "We do not sell, rent or trade your personal information.",
  },
  {
    title: "Overseas Processing",
    body: "Some service providers operate outside Australia, including in the United States. Your information may therefore be processed or stored overseas.",
  },
  {
    title: "Security",
    body: "We use reasonable technical and organisational measures to protect your information, including encryption, access controls, security monitoring and other safeguards.",
  },
  {
    title: "Data Retention",
    body: "We retain information only for as long as reasonably necessary, subject to legal, security, accounting and other legitimate requirements.",
  },
  {
    title: "Account Deletion",
    body: "You can request deletion of your account through the app. Deleted accounts enter a 30-day recovery period before permanent deletion, subject to information we are required or permitted to retain.",
  },
  {
    title: "Your Privacy Rights",
    body: "Subject to applicable law, you may request access to or correction of your personal information and may have other privacy rights.",
  },
  {
    title: "Privacy Complaints",
    body: "If you have a privacy concern, you can contact us and we will investigate and respond.",
  },
  {
    title: "Children",
    body: "Offcut may be used by apprentices and younger people. We handle children's personal information in accordance with applicable privacy laws.",
  },
  {
    title: "Data Breaches",
    body: "Where an eligible data breach occurs, we will comply with Australia's Notifiable Data Breaches requirements.",
  },
  {
    title: "Changes",
    body: "We may update the Privacy Policy from time to time and will provide notice of material changes where required by law.",
  },
];

export default function Privacy() {
  return (
    <motion.main
      data-testid="privacy-page"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
      className="relative z-10 mx-auto max-w-3xl px-6 pb-32 pt-32 sm:px-10"
    >
      <Link
        to="/"
        data-testid="page-logo-home-link"
        aria-label="Back to home"
        className="block w-fit rounded-lg transition-transform duration-300 hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
      >
        <img
          src="/chippymate-logo.png"
          alt="Offcut — back to home"
          className="h-auto w-28"
        />
      </Link>
      <p className="mt-2 text-xs font-semibold uppercase tracking-[0.35em] text-white/50">
        Offcut - ChippyMate
      </p>
      <h1 className="font-brand mt-3 text-6xl tracking-wide text-white sm:text-7xl">
        <RevealLine>Privacy Policy</RevealLine>
      </h1>
      <p className="mt-6 text-xs font-semibold uppercase tracking-[0.2em] text-white/50">
        Last updated: 2026
      </p>
      <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/70 sm:text-base">
        This summary is provided for convenience only. Our full Privacy Policy
        contains the detailed information about how Offcut handles personal
        information.
      </p>

      <motion.a
        data-testid="privacy-pdf-download"
        href="/privacy-policy.pdf"
        target="_blank"
        rel="noopener noreferrer"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="mt-8 inline-flex items-center gap-2.5 rounded-full border border-white/20 bg-black/30 px-6 py-3 text-sm font-bold text-white backdrop-blur-xl transition-colors duration-300 hover:border-white/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
      >
        <FileDown className="h-4 w-4" />
        Full Privacy Policy (PDF)
      </motion.a>

      <div className="mt-16 space-y-12">
        {SECTIONS.map((section, i) => (
          <motion.section
            key={section.title}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
            className="border-t border-white/10 pt-8"
          >
            <div className="flex items-baseline gap-5">
              <span className="font-brand text-2xl text-white/35">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h2 className="font-brand text-3xl tracking-wide text-white sm:text-4xl">
                {section.title}
              </h2>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-white/70 sm:text-base">
              {section.body}
            </p>
          </motion.section>
        ))}
      </div>

      <motion.p
        initial={{ opacity: 0, y: 28 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.6, ease: [0.76, 0, 0.24, 1] }}
        className="mt-16 border-t border-white/10 pt-8 text-sm leading-relaxed text-white/70 sm:text-base"
      >
        If you have any questions about the Privacy Policy or how your
        information is handled, please reach out via the{" "}
        <Link
          to="/contact"
          className="font-semibold text-white underline decoration-white/40 underline-offset-4 transition-colors duration-300 hover:decoration-white"
        >
          Contact Us
        </Link>{" "}
        page.
      </motion.p>

      <LegalCrossLinks current="/privacy" />
    </motion.main>
  );
}
